import React, { useEffect } from 'react';
import { useContentfulActionCards } from './hooks/useContentfulActionCards';
import LoadingBanner from '../ActionCentre/Banners/Loading/LoadingBanner';
import { useAnalytics } from '@ovotech/ui-tools';
import { CUSTOMER_ACTION_CARD_EVENTS } from '@/src/constants/analytics';
import { ServiceActionCodes } from './hooks/utils/rulesetForServiceBanners';
import { ActionCardContent, ActionCardView } from './ActionCardView';

type Props = {
  cardsToShowByActionCode?: Array<string | ServiceActionCodes>;
  utmSource?: string;
};

export const ActionCard = ({ cardsToShowByActionCode, utmSource }: Props) => {
  const { isLoading, activeCard, reject, accept, present } =
    useContentfulActionCards(cardsToShowByActionCode);
  const { dispatch } = useAnalytics();

  const actionCode = activeCard?.actionCode;
  const contentfulCard = activeCard?.data;
  const FallbackComponent = activeCard?.fallbackComponent;
  const hasData = actionCode && (!!contentfulCard?.title || FallbackComponent);
  const isContentfulActionCard = !!contentfulCard?.title;

  useEffect(() => {
    if (!isLoading && hasData) {
      dispatch({
        name: CUSTOMER_ACTION_CARD_EVENTS.cardShown,
        type: 'view',
        properties: {
          cardName: activeCard.mixpanelId,
          contentfulActionCard: `${isContentfulActionCard}`,
        },
      });
      present(actionCode);
    }
    // dispatch is not stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actionCode,
    present,
    hasData,
    activeCard?.mixpanelId,
    isContentfulActionCard,
    isLoading,
  ]);

  if (isLoading) return <LoadingBanner />;
  if (!hasData) return null;

  const handleOnClickClose = () => {
    reject(actionCode, activeCard?.onDismiss);
    dispatch({
      name: CUSTOMER_ACTION_CARD_EVENTS.cardDismissed,
      type: 'click',
      properties: {
        cardName: activeCard?.mixpanelId,
        contentfulActionCard: `${isContentfulActionCard}`,
      },
    });
  };

  const handleOnClickCta = () => {
    accept(actionCode, activeCard?.onDismiss);
    dispatch({
      name: CUSTOMER_ACTION_CARD_EVENTS.cardTapped,
      type: 'click',
      properties: {
        cardName: activeCard?.mixpanelId,
        contentfulActionCard: `${isContentfulActionCard}`,
      },
    });
  };

  // Try render card from Contentful data first
  if (contentfulCard?.title) {
    const mappedCardData: ActionCardContent = {
      title: contentfulCard.title,
      body: contentfulCard.body,
      inverted: !!contentfulCard.inverted,
      nonRoundedEdges: !!contentfulCard.nonRoundedEdges,
      canBeDismissed: !!contentfulCard.canBeDismissed,
      image: contentfulCard.image
        ? {
            url: contentfulCard.image.url ?? undefined,
            width: contentfulCard.image.width ?? undefined,
          }
        : undefined,
      cta: contentfulCard.cta
        ? {
            label: contentfulCard.cta.label ?? undefined,
            action: contentfulCard.cta.action ?? undefined,
          }
        : undefined,
    };

    return (
      <ActionCardView
        cardData={mappedCardData}
        onClose={handleOnClickClose}
        onCtaClick={handleOnClickCta}
      />
    );
  }

  // Fallback to hardcoded cards if no Contentful card
  if (FallbackComponent) {
    return (
      <FallbackComponent
        dismiss={handleOnClickClose}
        onClick={handleOnClickCta}
        utmSource={utmSource}
      />
    );
  }

  return null;
};
